export const collectConditionFields = (cond) => {
    if (!cond) return [];
    if (cond.field) return [cond.field];
    const keys = ['all', 'any'];
    for (const k of keys) {
        if (Array.isArray(cond[k])) {
            return cond[k].flatMap(collectConditionFields);
        }
    }
    return [];
};

export const evalSingle = (c, values) => {
    const left = (values[c.field] ?? '').toString().trim();
    const op = c.operator;

    if (op === 'equals') {
        const right = (c.value ?? '').toString().trim();
        return left.localeCompare(right, undefined, { sensitivity: 'accent' }) === 0;
    }
    if (op === 'in') {
        const arr = Array.isArray(c.values) ? c.values : [];
        return arr.some(v => left.localeCompare((v ?? '').toString().trim(), undefined, { sensitivity: 'accent' }) === 0);
    }
    if (op === 'notEmpty') return !isFieldEffectivelyEmpty(c.field, values[c.field]);
    if (op === 'empty') return isFieldEffectivelyEmpty(c.field, values[c.field]);
    return false;
};

export const evaluateCompositeCondition = (cond, values) => {
    if (!cond) return false;
    if (cond.field) return evalSingle(cond, values);
    if (Array.isArray(cond.all)) return cond.all.every(c => evaluateCompositeCondition(c, values));
    if (Array.isArray(cond.any)) return cond.any.some(c => evaluateCompositeCondition(c, values));
    return false;
};


export const evaluateShowIfCondition = (group, fieldValues) => {
    const condition = group.ShowIf;
    if (!condition) return false;

    // allow composite ShowIf like { all:[...]} / { any:[...] }
    // Reuse existing evaluator so we can do multi-field conditions.
    if (Array.isArray(condition.all) || Array.isArray(condition.any)) {
        return evaluateCompositeCondition(condition, fieldValues);
    }

    const raw = fieldValues[condition.field];
    const val = (raw ?? '').toString().trim();

    // generic string equals
    if (condition.operator === 'equals') {
        const target = (condition.value ?? '').toString().trim();
        return val.localeCompare(target, undefined, { sensitivity: 'accent' }) === 0;
    }

    // simple emptiness checks
    if (condition.operator === 'empty') {
        return isFieldEffectivelyEmpty(condition.field, raw);
    }

    if (condition.operator === 'notEmpty') {
        return !isFieldEffectivelyEmpty(condition.field, raw);
    }

    // date vs today
    if (!val) return false;

    const normalizeDate = (date) => {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    };

    const fieldDateObj = normalizeDate(new Date(val));
    const todayObj = normalizeDate(new Date());

    if (isNaN(fieldDateObj)) return false;

    switch (condition.operator) {
        case 'lt':
            return fieldDateObj < todayObj;
        case 'lte':
            return fieldDateObj <= todayObj;
        case 'eq':
            return fieldDateObj.getTime() === todayObj.getTime();
        case 'gte':
            return fieldDateObj >= todayObj;
        case 'gt':
            return fieldDateObj > todayObj;
        default:
            return false;
    }
};


export const isEmptyField = (val) => {
    const trimmed = (val || '').trim();
    return trimmed === '' || trimmed === '//' || trimmed === '  /  /    ';
};

// honor JSON field-level validators (e.g., validate: "notTBD")
export const violatesValidateRule = (field, value) => {
    if (!field?.validate) return false;
    const v = (value ?? '').toString().trim();
    if (field.validate === 'notTBD') {
        return v.toUpperCase() === 'TBD';
    }
    return false;
};

export const isInvalidNumber = (val, allowZero = false) => {
    if (val === null || val === undefined) return true;

    const trimmed = String(val).trim();
    if (trimmed === '') return true;

    const normalized = trimmed.replace(/,/g, '');
    const num = Number(normalized);

    if (Number.isNaN(num)) return true;

    return allowZero ? num < 0 : num <= 0;
};

export const isFieldEffectivelyEmpty = (fieldId, val) => {
    const id = String(fieldId);

    // Special handling for 389 (allow 0)
    if (id === '389') {
        return isInvalidNumber(val, true); // allow zero
    }

    // Other numeric fields (must be > 0)
    if (
        id === 'LCP.X8' ||
        id === '1621' ||
        id === 'NEWHUD.X139'
    ) {
        return isInvalidNumber(val, false);
    }

    const trimmed = (val ?? '').toString().trim();
    return trimmed === '' || trimmed === '//' || trimmed === '  /  /    ';
};